import React from 'react';
import { Link } from 'react-router-dom';

// Agent data for the AI Teammates column
const agents = [
  { name: 'Buzz', role: 'Social Media Expert', path: '/buzz-ai-social-media-expert' },
  { name: 'Dora', role: 'Research & Data Analyst', path: '/dora-ai-research-data-analyst' },
  { name: 'Finn', role: 'Finance Expert', path: '/finn-ai-finance-expert' },
  { name: 'Luc', role: 'Personal Growth Partner', path: '/luc-ai-personal-growth-partner' },
  { name: 'Marsi', role: 'Marketing Growth Expert', path: '/marsi-ai-marketing-growth-expert' },
  { name: 'Sally', role: 'Sales Expert', path: '/sally-ai-sales-expert' },
  { name: 'Stark', role: 'Business Strategist', path: '/stark-ai-business-strategist' },
  { name: 'Tali', role: 'HR & People Partner', path: '/tali-ai-hr-people-partner' },
  { name: 'Wright', role: 'Copywriting Expert', path: '/wright-ai-copywriting-expert' },
  { name: 'Zira', role: 'Support Expert', path: '/zira-ai-support-expert' },
];

// Use Cases sub-pages (no hub since CP4 — the mega-menu is the entry point). Listed
// individually in the footer for navigation + internal-linking / SEO.
const useCaseLinks = [
  { label: 'Sales', path: '/use-cases/sales' },
  { label: 'Marketing', path: '/use-cases/marketing' },
  { label: 'Personal Productivity', path: '/use-cases/personal-productivity' },
  { label: 'CRM Management', path: '/use-cases/crm-management' },
  { label: 'Pipeline Generation', path: '/use-cases/pipeline-generation' },
  { label: 'Focus & Flow', path: '/use-cases/focus-and-flow' },
];

const Footer = ({ variant = 'light' }) => {
  // variant: 'dark' for dark backgrounds (homepage, agent pages), 'light' for light backgrounds (blog),
  //          'minimal' for distraction-free paid-search landing pages (no nav — spec §6.4)
  if (variant === 'minimal') {
    return (
      <footer
        className="border-t"
        style={{ borderColor: 'rgba(20, 20, 43, 0.1)', background: 'transparent' }}
        data-testid="footer-minimal"
      >
        <div className="mx-auto flex max-w-5xl flex-col items-center justify-between gap-4 px-6 py-8 sm:flex-row">
          <p className="text-xs" style={{ color: 'rgba(20, 20, 43, 0.5)' }}>
            © 2025 Implify. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link
              to="/privacy-policy"
              className="text-xs transition-colors duration-200 hover:text-[#14142b]"
              style={{ color: 'rgba(20, 20, 43, 0.6)' }}
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms-of-service"
              className="text-xs transition-colors duration-200 hover:text-[#14142b]"
              style={{ color: 'rgba(20, 20, 43, 0.6)' }}
            >
              Terms of Service
            </Link>
          </div>
        </div>
      </footer>
    );
  }

  const isLight = variant === 'light';
  
  const darkLogo = "/images/logo-dark.webp";
  const whiteLogo = "/images/logo-white-header.webp";

  // Light footer stays TRANSPARENT so the page's aurora shows through unbroken (no
  // gray band / seam); only the text flips to the dark sweep palette.
  const styles = {
    footer: isLight
      ? { borderColor: 'rgba(20, 20, 43, 0.1)', background: 'transparent' }
      : { borderColor: 'rgba(255, 255, 255, 0.1)', background: 'transparent' },
    heading: { color: isLight ? '#14142b' : 'white' },
    text: { color: isLight ? 'rgba(20, 20, 43, 0.7)' : 'rgba(255, 255, 255, 0.7)' },
    link: { color: isLight ? 'rgba(20, 20, 43, 0.65)' : 'rgba(255, 255, 255, 0.6)' },
    linkHover: isLight ? 'hover:text-[#14142b]' : 'hover:text-white',
    border: { borderColor: isLight ? 'rgba(20, 20, 43, 0.1)' : 'rgba(255, 255, 255, 0.1)' },
    copyright: { color: isLight ? 'rgba(20, 20, 43, 0.5)' : 'rgba(255, 255, 255, 0.5)' },
  };

  return (
    <footer className="border-t" style={styles.footer} data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-12">
          {/* Logo & Description Column */}
          <div className="md:col-span-3 lg:col-span-2">
            <img
              src={isLight ? darkLogo : whiteLogo}
              alt="Implify"
              className="h-8 w-auto mb-4"
              loading="lazy"
            />
            <p className="text-sm mb-3" style={styles.text}>
              Your autonomous AI workforce. Built for teams who ship.
            </p>
            <p className="text-sm" style={styles.text}>
              Implify provides specialized AI agents for marketing, sales, finance, HR, research, social media, and more.
            </p>
          </div>

          {/* Platform Column — the new revamp primary pages (7.1 footer wiring) */}
          <div>
            <h3 className="text-sm font-medium mb-4" style={styles.heading}>
              Platform
            </h3>
            <ul className="space-y-2">
              <li>
                <Link to="/how-it-works" className={`text-sm transition-colors duration-200 ${styles.linkHover}`} style={styles.link}>
                  How It Works
                </Link>
              </li>
              <li>
                <Link to="/services" className={`text-sm transition-colors duration-200 ${styles.linkHover}`} style={styles.link}>
                  Services
                </Link>
              </li>
              <li>
                <Link to="/pricing" className={`text-sm transition-colors duration-200 ${styles.linkHover}`} style={styles.link}>
                  Pricing
                </Link>
              </li>
            </ul>
          </div>

          {/* Use Cases Column — no hub page (removed CP4), so the header is plain text and
              the 6 sub-pages are listed under it, mirroring the AI Teammates column. */}
          <div>
            <h3 className="text-sm font-medium mb-4" style={styles.heading}>
              Use Cases
            </h3>
            <ul className="space-y-2">
              {useCaseLinks.map((uc) => (
                <li key={uc.path}>
                  <Link to={uc.path} className={`text-sm transition-colors duration-200 ${styles.linkHover}`} style={styles.link}>
                    {uc.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* AI Teammates Column */}
          <div>
            <h3 className="text-sm font-medium mb-4" style={styles.heading}>
              AI Teammates
            </h3>
            <ul className="space-y-2">
              {agents.map((agent) => (
                <li key={agent.name}>
                  <Link
                    to={agent.path}
                    className={`text-sm transition-colors duration-200 ${styles.linkHover}`}
                    style={styles.link}
                  >
                    {agent.name} - {agent.role}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Column — company links + legal (merged to keep a clean 6-col grid) */}
          <div>
            <h3 className="text-sm font-medium mb-4" style={styles.heading}>
              Company
            </h3>
            <ul className="space-y-2">
              <li>
                <Link to="/about" className={`text-sm transition-colors duration-200 ${styles.linkHover}`} style={styles.link}>
                  About
                </Link>
              </li>
              <li>
                <Link to="/blog" className={`text-sm transition-colors duration-200 ${styles.linkHover}`} style={styles.link}>
                  Blog
                </Link>
              </li>
              <li>
                <a href="mailto:sales@implifyai.com" className={`text-sm transition-colors duration-200 ${styles.linkHover}`} style={styles.link}>
                  Contact
                </a>
              </li>
              <li>
                <Link to="/privacy-policy" className={`text-sm transition-colors duration-200 ${styles.linkHover}`} style={styles.link}>
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms-of-service" className={`text-sm transition-colors duration-200 ${styles.linkHover}`} style={styles.link}>
                  Terms of Service
                </Link>
              </li>
              <li>
                <button
                  onClick={() => {
                    // Dispatch custom event to open cookie preferences
                    window.dispatchEvent(new CustomEvent('openCookiePreferences'));
                  }}
                  className={`text-sm transition-colors duration-200 ${styles.linkHover}`}
                  style={styles.link}
                  data-testid="footer-cookie-settings"
                >
                  Cookie Settings
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t flex flex-col md:flex-row justify-between items-center gap-4" style={styles.border}>
          <p className="text-sm" style={styles.copyright}>
            © 2025 Implify. All rights reserved.
          </p>
          <div className="flex gap-6">
            <a 
              href="https://www.instagram.com/implify.ai" 
              target="_blank" 
              rel="noopener noreferrer"
              className={`text-sm transition-colors duration-200 ${styles.linkHover}`} 
              style={styles.link}
              data-testid="footer-instagram"
              aria-label="Follow Implify on Instagram"
            >
              Instagram
            </a>
            <a 
              href="https://x.com/implifyai" 
              target="_blank" 
              rel="noopener noreferrer"
              className={`text-sm transition-colors duration-200 ${styles.linkHover}`} 
              style={styles.link}
              data-testid="footer-x"
              aria-label="Follow Implify on X (formerly Twitter)"
            >
              X
            </a>
            <a 
              href="https://www.linkedin.com/company/implifyai" 
              target="_blank" 
              rel="noopener noreferrer"
              className={`text-sm transition-colors duration-200 ${styles.linkHover}`} 
              style={styles.link}
              data-testid="footer-linkedin"
              aria-label="Follow Implify on LinkedIn"
            >
              LinkedIn
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
